/* eslint-disable no-shadow */
import Vue from 'vue';
// eslint-disable-next-line import/no-cycle
import ApiService from '@/services/api.service';
import { apiEndpoint } from '@/shared';
import { PMSError, ValidationError } from '@/errors';

const endpoint = `${apiEndpoint}/reservations`;

const state = {
  data: null,
  newdata: null,
  pending: false,
  error: null,
  pmsError: null,
  creditCardDetails: null,
  creditCardCCPending: null,
  searchOptionProducts: null,
  searchOptionRooms: null,
  searchOptionChannels: null,
  fetchingSearchOptions: false,
  reportData: null,
  reportDataPending: false,
};

const getters = {
  loaded: (state) => !state.pending,
  plans: (state, getters) => (getters.loaded ? state.data.plans : []),
  policies: (state, getters) => (getters.loaded ? state.data.policies : []),
  empty: (state) => state.data && state.data.list != null && !state.data.list.length,
  iframeLoaded: (state) => state.creditCardDetails != null,
};

const mutations = {
  clearErrors(state) {
    state.error = null;
  },
  beforeLoading(state) {
    state.error = null;
    state.pending = true;
  },
  beforeReportDataRequest(state) {
    state.error = null;
    state.reportDataPending = true;
  },
  beforeCreditCardCCLoading(state) {
    state.error = null;
    state.creditCardCCPending = true;
  },
  updateCreditCardCC(state, data) {
    const record = state.data ? state.data.list.findIndex((b) => b.id === data.id) : null;
    if (record !== -1) {
      state.data.list[record].cc_data = {
        is_cc_existed: true,
        is_cc_valid: false,
        sent_time: new Date(),
      };
    }
    state.creditCardCCPending = false;
  },
  error(state, error) {
    state.pending = false;
    state.reportDataPending = false;
    state.error = error;
    state.pmsError = error;
  },
  data(state, data) {
    state.pending = false;
    state.data = data;
  },
  reportData(state, data) {
    state.reportDataPending = false;
    state.reportData = data;
  },
  newdata(state, data) {
    state.pending = false;
    state.newdata = data;
  },
  update(state, booking) {
    const record = state.data ? state.data.list.find((b) => b.id === booking.id) : null;
    if (record != null) {
      Object.keys(booking).forEach((k) => {
        record[k] = booking[k];
      });
    }
    state.pending = false;
  },
  updatePolicies(state, data) {
    state.data.cancels = data.cancels;
    state.data.policies = data.policies;
  },
  updateSearchOptionProducts(state, data) {
    state.searchOptionProducts = data;
  },
  updateSearchOptionRooms(state, data) {
    state.searchOptionRooms = data;
  },
  updateSearchOptionChannels(state, data) {
    state.searchOptionChannels = data;
  },
  updatePlanAndRoom(state, data) {
    const idx = state.newdata.plans.findIndex((p) => p.id === data.plans.id);
    if (idx !== -1) {
      Vue.set(state.newdata.plans, idx, JSON.parse(JSON.stringify(data.plans)));
    }
    state.newdata.rooms = data.rooms;
  },
  fetchIframe(state, data) {
    state.pending = false;
    state.creditCardDetails = data;
  },
  beforeFetchingSearchOptions(state) {
    state.fetchingSearchOptions = true;
  },
  fetchedSearchOptions(state) {
    state.fetchingSearchOptions = false;
  },
};

function handleApiError(commit, error, commitError = true) {
  const { status, data } = error.response;
  let err = error;
  switch (status) {
    case 422:
      err = new ValidationError(status, data.message, data.errors);
      break;
    case 409: // PMSError
      err = new PMSError(status, data.message);
      break;
    case 500: // PMSError
      err = new Error(data.message);
      break;
    default:
      break;
  }
  if (commitError) {
    commit('error', err);
  }
  throw err;
}

const actions = {
  async fetchAllData({ commit }, payload) {
    commit('beforeLoading');
    try {
      const response = await ApiService.post(`${endpoint}`, payload);
      commit('data', response.data);
    } catch (error) {
      commit('error', error);
      handleApiError(commit, error);
    }
  },
  async fetchReportData({ commit }, payload) {
    commit('beforeReportDataRequest');
    try {
      const response = await ApiService.post(`${endpoint}`, payload);
      commit('reportData', response.data);
    } catch (error) {
      commit('error', error);
      handleApiError(commit, error);
    }
  },
  async cancelReservation({ commit }, payload) {
    commit('beforeLoading');
    try {
      const response = await ApiService.patch(`${endpoint}/cancel`, payload);
      commit('update', response.data);
    } catch (error) {
      handleApiError(commit, error);
    }
  },
  async updateCreditCardCC({ commit }, payload) {
    commit('beforeCreditCardCCLoading');
    try {
      await ApiService.patch(`${endpoint}/credit-card-cc`, payload);
      commit('updateCreditCardCC', payload);
    } catch (error) {
      handleApiError(commit, error);
    }
  },
  async fetchIframe({ commit }, payload) {
    // commit('beforeLoading');
    try {
      const response = await ApiService.post(`${endpoint}/card-details`, payload);
      commit('fetchIframe', response.data);
    } catch (error) {
      handleApiError(commit, error, false);
    }
  },
  async getModalEmails({ commit }, { reservationID, cultswitchReservationID }) {
    const URL = `${endpoint}/emails/${reservationID}/${cultswitchReservationID}`;
    let response = {};

    try {
      response = await ApiService.get(URL);
    } catch (error) {
      commit('error', error);
    }

    return response.data;
  },
  async sendNewReservationEmail({ commit }, payload) {
    const URL = `${endpoint}/emails`;
    let response = {};

    try {
      response = await ApiService.post(URL, payload);
    } catch (error) {
      commit('error', error);
    }

    return response.data;
  },
  async resentBookingToPms({ commit }, payload) {
    const URL = `${endpoint}/resent-booking-to-pms`;
    try {
      await ApiService.post(URL, payload);
    } catch (error) {
      commit('error', error);
    }
  },
  async resentBookingConfirmation({ commit }, payload) {
    const URL = `${endpoint}/resent-booking-confirmation`;
    try {
      await ApiService.post(URL, payload);
    } catch (error) {
      commit('error', error);
    }
  },
  async getSearchOptionProducts({ commit }) {
    commit('beforeFetchingSearchOptions');
    try {
      const response = await ApiService.get(`${endpoint}/search-option-products`);
      commit('updateSearchOptionProducts', response.data);
    } catch (error) {
      commit('error', error);
      handleApiError(commit, error);
    } finally {
      commit('fetchedSearchOptions');
    }
  },
  async getSearchOptionRooms({ commit }) {
    commit('beforeFetchingSearchOptions');
    try {
      const response = await ApiService.get(`${endpoint}/search-option-rooms`);
      commit('updateSearchOptionRooms', response.data);
    } catch (error) {
      commit('error', error);
      handleApiError(commit, error);
    } finally {
      commit('fetchedSearchOptions');
    }
  },
  async getSearchOptionChannels({ commit }) {
    commit('beforeFetchingSearchOptions');
    try {
      const response = await ApiService.get(`${endpoint}/search-option-channels`);
      commit('updateSearchOptionChannels', response.data);
    } catch (error) {
      commit('error', error);
      handleApiError(commit, error);
    } finally {
      commit('fetchedSearchOptions');
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
